import React from 'react';

import {
    CollapsibleContent,
    CollapsibleContentFull,
    CollapsibleContentIntro,
} from '@dndbeyond/character-common-components/es';

import { appEnvSelectors } from '../../../../Shared/selectors';
import Page from '../../../components/Page';
import PageBody from '../../../components/PageBody';
import PageHeader from '../../../components/PageHeader';
import { RouteKey } from '../../../constants/navigation';
import { BuilderAppState } from '../../../typings';
import ConnectedBuilderPage from '../ConnectedBuilderPage';

interface Props {
    isMobile: boolean;
}
class RaceHelp extends React.PureComponent<Props> {
    render() {
        const { isMobile } = this.props;

        return (
            <Page>
                <PageBody>
                    <PageHeader>Choose a Race</PageHeader>

                    <CollapsibleContent forceShow={!isMobile}>
                        <CollapsibleContentIntro>
                            <p>
                                Choose a race in the next step. Your choice of race affects many different aspects of
                                your character. It establishes fundamental qualities that exist throughout your
                                character's adventuring career. When making this decision, keep in mind the kind of
                                character you want to play.
                            </p>
                        </CollapsibleContentIntro>
                        <CollapsibleContentFull>
                            <p>
                                For example, a halfling could be a good choice for a sneaky rogue, a dwarf makes a tough
                                warrior, and an elf can be a master of arcane magic.
                            </p>

                            <p>
                                Your character race not only affects your ability scores and traits but also provides
                                the cues for building your character's story. Each race's description includes
                                information to help you roleplay a character of That race, including personality,
                                physical appearance, features of society, and racial alignment tendencies.
                            </p>

                            <p>
                                These details are suggestions to help you think about your character; adventurers can
                                deviate widely from the norm for their race. It's worthwhile to consider why your
                                character is different, as a helpful way to think about your character's background and
                                personality.
                            </p>
                        </CollapsibleContentFull>
                    </CollapsibleContent>

                    <PageHeader>Racial Traits</PageHeader>

                    <CollapsibleContent forceShow={!isMobile}>
                        <CollapsibleContentIntro>
                            <p>
                                The description of each race includes racial traits that are common to members of that
                                race. The following entries appear among the traits of most races.
                            </p>
                        </CollapsibleContentIntro>
                        <CollapsibleContentFull>
                            <p>
                                <strong>ABILITY SCORE INCREASE</strong>
                                <br />
                                Every race increases one or more of a character's ability scores.
                            </p>

                            <p>
                                <strong>AGE</strong>
                                <br />
                                The age entry notes the age when a member of the race is considered an adult, as well as
                                the race's expected lifespan. This information can help you decide how old your
                                character is at the start of the game. You can choose any age for your character, which
                                could provide an explanation for some of your ability scores.
                            </p>

                            <p>
                                For example, if you play a young or very old character, your age could explain a
                                particularly low Strength or Constitution score, while advanced age could account for a
                                high Intelligence or Wisdom.
                            </p>

                            <p>
                                <strong>ALIGNMENT</strong>
                                <br />
                                Most races have tendencies toward certain alignments, described in this entry. These are
                                not binding for player characters, but considering why your dwarf is chaotic, for
                                example, in defiance of lawful dwarf society can help you better define your character.
                            </p>

                            <p>
                                <strong>SIZE</strong>
                                <br />
                                Characters of most races are Medium, a size category including creatures that are
                                roughly 4 to 8 feet tall. Members of a few races are Small (between 2 and 4 feet
                                tall),which means that certain rules of the game affect them differently. The most
                                important of these rules is that Small characters have trouble wielding heavy weapons,
                                as explained in the Equipment rules.
                            </p>

                            <p>
                                <strong>SPEED</strong>
                                <br />
                                Your speed determines how far you can move when traveling and fighting.
                            </p>

                            <p>
                                <strong>LANGUAGES</strong>
                                <br />
                                By virtue of your race, your character can speak, read, and write certain languages.
                            </p>

                            <p>
                                <strong>SUBRACES</strong>
                                <br />
                                Some races have subraces. Members of a subrace have the traits of the parent race in
                                addition to the traits specified for their subrace. Relationships among subraces vary
                                significantly from race to race and world to world. In the Dragonlance campaign setting,
                                for example, mountain dwarves and hill dwarves live together as different clans of the
                                same people, but in the Forgotten Realms, they live far apart in separate kingdoms and
                                call themselves shield dwarves and gold dwarves, respectively.
                            </p>
                        </CollapsibleContentFull>
                    </CollapsibleContent>
                </PageBody>
            </Page>
        );
    }
}

export default ConnectedBuilderPage(RaceHelp, RouteKey.RACE_HELP, (state: BuilderAppState) => {
    return {
        isMobile: appEnvSelectors.getIsMobile(state),
    };
});
